'use client'

import { useRef } from 'react'
import { motion, useInView, useReducedMotion } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import { Separator } from '@mdxui/primitives'
import { cn } from '../../lib/cn'
import { Markdown } from '../../lib/markdown'
import {
  sectionVariants,
  headerVariants,
  gridItemVariants,
  reducedMotionVariants,
} from '../../lib/motion'
import { CodeWindowHero } from '../code-window'

/**
 * Props for FeaturesGallery (Gallery) component
 *
 * @remarks
 * **IMPORTANT CONSTRAINTS FOR AI/PROGRAMMATIC GENERATION:**
 * - This component ONLY supports code windows, NOT images
 * - Code windows are displayed borderless without headers/filenames
 * - Requires exactly 3 code examples: 2 stacked (left) + 1 tall (right)
 * - Each code block needs `code` (string) and `language` (syntax highlighting)
 * - Best used for showing installation, import, and usage code examples
 * - Two content blocks below provide context for the code examples
 */
export interface FeaturesGalleryProps {
  /** Section heading (H2) - centered above the grid */
  heading?: string
  /** Section description - centered below heading */
  description?: string
  /**
   * First content block (bottom-left)
   * Provides context for the stacked code examples
   */
  content1?: {
    /** Small label text above heading */
    label?: string
    /** H3 heading for this content block */
    heading: string
    /** Description paragraph */
    description: string
    /** Optional "Learn more" link with arrow icon */
    learnMore?: { text: string; url: string }
  }
  /**
   * Second content block (bottom-right)
   * Provides context for the tall code example
   */
  content2?: {
    /** Small label text above heading */
    label?: string
    /** H3 heading for this content block */
    heading: string
    /** Description paragraph */
    description: string
    /** Optional "Learn more" link with arrow icon */
    learnMore?: { text: string; url: string }
  }
  /**
   * Stacked code windows (left column, top-left position)
   * Two code blocks separated by a divider
   * Best for: installation commands (top) + import statements (bottom)
   */
  stackedCode?: {
    /** Top code window - e.g., npm install commands */
    top: { code: string; language: string }
    /** Bottom code window - e.g., import statements */
    bottom: { code: string; language: string }
  }
  /**
   * Single tall code window (right column, top-right position)
   * Best for: full component usage example
   */
  tallCode?: { code: string; language: string }
  className?: string
}

/**
 * FeaturesGallery - Gallery layout with code windows and text content
 *
 * A 2x2 asymmetric grid showcasing code examples with explanatory content.
 * Designed specifically for developer-focused landing pages.
 *
 * **Layout:**
 * ```
 * ┌─────────────────┬─────────────────┐
 * │  Stacked Code   │                 │
 * │  ─────────────  │   Tall Code     │
 * │  Stacked Code   │                 │
 * ├─────────────────┼─────────────────┤
 * │  Content 1      │  Content 2      │
 * │  (label, h3,    │  (label, h3,    │
 * │   desc, link)   │   desc, link)   │
 * └─────────────────┴─────────────────┘
 * ```
 *
 * **CONSTRAINTS:**
 * - Code windows ONLY - no image support
 * - Borderless code windows without headers
 * - Dark theme syntax highlighting always
 * - Requires 3 code examples to look balanced
 *
 * **Best Use Cases:**
 * - Developer onboarding/getting started sections
 * - SDK/library documentation landing pages
 * - Showing install → import → use workflow
 *
 * @example
 * ```tsx
 * <FeaturesGallery
 *   heading="Get Started"
 *   description="Three steps to your first component"
 *   stackedCode={{
 *     top: { code: 'npm install mdxui', language: 'bash' },
 *     bottom: { code: "import { Hero } from 'mdxui'", language: 'typescript' },
 *   }}
 *   tallCode={{
 *     code: '<Hero title="Hello" />',
 *     language: 'tsx',
 *   }}
 *   content1={{
 *     label: 'Step 1 & 2',
 *     heading: 'Install and Import',
 *     description: 'Quick setup with npm or pnpm.',
 *   }}
 *   content2={{
 *     label: 'Step 3',
 *     heading: 'Use Components',
 *     description: 'Drop components into your app.',
 *   }}
 * />
 * ```
 */
const FeaturesGallery = ({
  heading = 'See it in action',
  description = 'Real code examples showing how easy it is to build with MDXUI components.',
  content1 = {
    label: 'Quick Setup',
    heading: 'Get Started in Minutes',
    description:
      'Install the package and start building immediately. No complex configuration required.',
    learnMore: { text: 'View installation guide', url: '#' },
  },
  content2 = {
    label: 'Full Customization',
    heading: 'Make It Your Own',
    description:
      'Every component is fully customizable with Tailwind CSS. Match your brand perfectly.',
    learnMore: { text: 'Explore customization options', url: '#' },
  },
  stackedCode = {
    top: {
      code: `npm install mdxui @mdxui/primitives

# or with pnpm
pnpm add mdxui @mdxui/primitives`,
      language: 'bash',
    },
    bottom: {
      code: `import { Hero, Features } from 'mdxui'
import { Button } from '@mdxui/primitives'`,
      language: 'typescript',
    },
  },
  tallCode = {
    code: `export default function LandingPage() {
  return (
    <>
      <Hero
        title="Ship faster"
        description="Production-ready"
        callToAction="Get Started"
      />
      <Features
        variant="gallery"
        features={features}
      />
    </>
  )
}`,
    language: 'tsx',
  },
  className,
}: FeaturesGalleryProps) => {
  const sectionRef = useRef<HTMLElement>(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' })
  const prefersReducedMotion = useReducedMotion()

  const headerAnimation = prefersReducedMotion
    ? reducedMotionVariants
    : headerVariants
  const itemAnimation = prefersReducedMotion
    ? reducedMotionVariants
    : gridItemVariants

  return (
    <motion.section
      ref={sectionRef}
      className={cn('relative bg-background py-16 sm:py-24', className)}
      variants={sectionVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
    >
      <div className="container max-w-7xl mx-auto">
        {/* Section header */}
        {(heading || description) && (
          <motion.div
            variants={headerAnimation}
            className="mx-auto mb-16 max-w-3xl text-center"
          >
            {heading && (
              <h2 className="text-3xl font-semibold lg:text-4xl">{heading}</h2>
            )}
            {description && (
              <div className="mt-4">
                <Markdown size="lg">{description}</Markdown>
              </div>
            )}
          </motion.div>
        )}

        <div className="mx-auto grid max-w-5xl gap-10 md:grid-cols-2">
          {/* Stacked code windows - top left */}
          <motion.div
            variants={itemAnimation}
            className="order-1 flex flex-col gap-1 sm:gap-2 overflow-hidden rounded-[var(--radius)] border border-border p-1 sm:p-2"
          >
            <CodeWindowHero
              code={stackedCode.top.code}
              language={stackedCode.top.language}
              showHeader={false}
              showCopy={false}
              theme="dark"
              style="borderless"
              size="full"
              className="aspect-video w-full rounded-[var(--radius)]"
            />
            <Separator />
            <CodeWindowHero
              code={stackedCode.bottom.code}
              language={stackedCode.bottom.language}
              showHeader={false}
              showCopy={false}
              theme="dark"
              style="borderless"
              size="full"
              className="aspect-video w-full rounded-[var(--radius)]"
            />
          </motion.div>

          {/* Tall code window - top right */}
          <motion.div
            variants={itemAnimation}
            className="order-3 flex h-full overflow-hidden rounded-[var(--radius)] border border-border p-1 sm:p-2 md:order-2"
          >
            <CodeWindowHero
              code={tallCode.code}
              language={tallCode.language}
              showHeader={false}
              showCopy={false}
              theme="dark"
              style="borderless"
              size="full"
              className="h-full w-full rounded-[var(--radius)]"
            />
          </motion.div>

          {/* Content blocks */}
          {[
            { content: content1, order: 'order-2 md:order-3' },
            { content: content2, order: 'order-4' },
          ].map(({ content, order }) => (
            <motion.div
              key={content.heading}
              variants={headerAnimation}
              className={cn('-mt-6 max-w-[412px]', order)}
            >
              {content.label && (
                <p className="mb-6 text-sm text-muted-foreground md:mb-12">
                  {content.label}
                </p>
              )}
              <h3 className="mb-3 text-2xl font-semibold md:mb-6">
                {content.heading}
              </h3>
              <p className="mb-6 text-muted-foreground">{content.description}</p>
              {content.learnMore && (
                <a
                  href={content.learnMore.url}
                  className="group inline-flex items-center gap-1 text-base hover:underline"
                >
                  {content.learnMore.text}
                  <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
                </a>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>
  )
}

export { FeaturesGallery }
